import React, { useState, useEffect } from 'react';
import { Box, Paper, Button, IconButton, Tooltip, useTheme, useMediaQuery, Chip, Fade, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Close as CloseIcon, ArrowForwardIos } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { admin } from '../../utils/api';

const SubNavbar = ({ section, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation('common');
  const [siteSettings, setSiteSettings] = useState({
    siteName: 'CAMATS TEAM RACING'
  });
  
  // Add useEffect to fetch site settings
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const settings = await admin.getPublicSiteSettings();
        setSiteSettings(settings);
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };

    fetchSiteSettings();
  }, []);

  // Don't render on mobile as we handle it in the drawer
  if (isMobile) return null;

  // Get the section configuration from the parent Navbar with proper routes from App.js
  const sectionConfig = {
    pitbox: {
      subsections: [
        { name: t('navigation.workshop'), route: '/workshop', enabled: true },
        { name: t('navigation.cars'), route: '/cars', enabled: true },
        { name: t('navigation.events'), route: '/events', enabled: true },
        { name: t('navigation.gallery'), route: '/galleries', enabled: true },
        { name: t('navigation.racetracks'), route: '/racetracks', enabled: true },
      ]
    }
  }[section] || { subsections: [] };

  const handleNavigation = (route, enabled) => {
    if (enabled && route) {
      navigate(route);
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ 
        duration: 0.3, 
        ease: [0.4, 0, 0.2, 1] // Material Design easing
      }}
    >
      <Paper
        elevation={3}
        sx={{
          backgroundColor: 'primary.dark',
          borderRadius: 0,
          position: 'relative',
          zIndex: theme => theme.zIndex.drawer - 1,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            <AnimatePresence mode="wait">
              {sectionConfig.subsections.map((subsection, index) => (
                <motion.div
                  key={subsection.name}
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  transition={{ 
                    duration: 0.3,
                    delay: index * 0.05, // Stagger effect
                    ease: [0.4, 0, 0.2, 1]
                  }}
                >
                  {subsection.enabled ? (
                    <Button
                      color="inherit"
                      onClick={() => handleNavigation(subsection.route, subsection.enabled)}
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        px: 2,
                        py: 0.75,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        },
                        '&:after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          width: '0%',
                          height: '2px',
                          backgroundColor: 'secondary.main',
                          transition: 'all 0.3s ease',
                        },
                        '&:hover:after': {
                          width: '100%',
                          left: '0%',
                        },
                        textTransform: 'none',
                        fontWeight: 'normal',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {subsection.name}
                    </Button>
                  ) : (
                    <Tooltip 
                      title={t('common.comingSoon')}
                      enterDelay={500}
                      leaveDelay={200}
                      TransitionComponent={Fade}
                      arrow
                    >
                      <Chip
                        label={subsection.name}
                        variant="outlined"
                        size="medium"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          },
                          cursor: 'default',
                          fontWeight: 'normal',
                          px: 1
                        }}
                      />
                    </Tooltip>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </Box>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.2 }}
            whileHover={{ rotate: 90 }}
          >
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ 
                color: 'inherit', 
                flexShrink: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </motion.div>
        </Box>
      </Paper>
    </motion.div>
  );
};

export default SubNavbar; 